import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Chip,
  Paper,
  Checkbox,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  fetchUserFormAnswers,
  fetchUserNotesById,
  fetchUserSlackProfile,
  previewUserActionItems,
  applyUserActionItems,
} from '../../../services/api/endpoints/users';
import { useAuth } from '../../../contexts/AuthContext';
import {
  FormAnswerItem,
  UserNoteResponse,
  SlackProfileField,
} from '../../../types';

type Props = {
  userId: string;
};

export default function UserProfileInsights({ userId }: Props) {
  const { user: authUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formAnswers, setFormAnswers] = useState<FormAnswerItem[]>([]);
  const [notes, setNotes] = useState<UserNoteResponse[]>([]);
  const [slackProfile, setSlackProfile] = useState<SlackProfileField[]>([]);
  const [actionItems, setActionItems] = useState<{ action: string; reasoning: string; task_type?: string }[]>([]);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applySuccess, setApplySuccess] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [justSaved, setJustSaved] = useState<number[]>([]);

  useEffect(() => {
    const loadAll = async () => {
      try {
        if (!authUser?.token) return;
        setLoading(true);
        setError(null);

        const [formRes, notesRes, slackRes] = await Promise.all([
          fetchUserFormAnswers(authUser.token, userId),
          fetchUserNotesById(authUser.token, userId),
          fetchUserSlackProfile(authUser.token, userId),

        ]);

        setFormAnswers(formRes.items);
        setNotes(notesRes);
        setSlackProfile(slackRes.profile);
      } catch (err: any) {
        console.error(err);
        setError(err.message || 'Failed to load user insights');
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, [userId, authUser?.token]);


const handlePreviewActionItems = async () => {
  if (!authUser?.token) return;

  setPreviewLoading(true);

  try {
    const res = await previewUserActionItems(authUser.token, userId);
    setActionItems(res.items);
  } catch (err) {
    console.error(err);
  } finally {
    setPreviewLoading(false);
  }
};



  if (loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight={600}>
        User Insights
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {/* Slack Profile */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="medium">
            Slack Profile
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {slackProfile.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {slackProfile.map((field) => (
                <Box
                  key={field.element_id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    p: 1.2,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" fontWeight="500">
                    {field.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {field.value || '—'}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No Slack profile fields found.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Form Answers */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="medium">
            Form Answers
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {formAnswers.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {formAnswers.map((a) => (
                <Box
                  key={a.id}
                  sx={{
                    p: 1.2,
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                  }}
                >
                  <Typography variant="body2" fontWeight="500">
                    {a.question_title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {a.value?.answer || '—'}
                  </Typography>
                  {a.form_title && (
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      {a.form_title}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No form answers found.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
      
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>

        <Typography variant="subtitle1" fontWeight="medium">
          Suggested Action Items
        </Typography>

      </AccordionSummary>
          <Button
            variant="contained"
            onClick={handlePreviewActionItems}
            sx={{ ml: 2, mt: 1 }}
            disabled={previewLoading}
            startIcon={previewLoading ? <CircularProgress size={18} /> : null}
          >
            {previewLoading ? "Generating..." : "Generate Suggested Action Items"}
          </Button>


      <AccordionDetails>
      {actionItems.map((item, idx) => {
        const isSelected = selectedItems.includes(idx);
        const isSaved = justSaved.includes(idx);

        return (
          <Box
            key={idx}
            sx={{
              p: 1.5,
              bgcolor: isSaved ? "success.light" : "grey.50",
              borderRadius: 1,
              display: "flex",
              gap: 1,
              opacity: isSaved ? 0.6 : 1,
              transition: "all 0.3s ease",
            }}
          >
            <Checkbox
              checked={isSelected}
              disabled={isSaved}
              onChange={() => {
                setSelectedItems((prev) =>
                  prev.includes(idx)
                    ? prev.filter((v) => v !== idx)
                    : [...prev, idx]
                );
              }}
            />

            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight={600}>
                {item.action}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {item.reasoning}
              </Typography>
            </Box>

            {isSaved && (
              <Typography color="success.main" fontWeight={700}>
                ✔
              </Typography>
            )}
          </Box>
        );
      })}

        {selectedItems.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            disabled={applyLoading}
            onClick={async () => {
              setApplyLoading(true);
              setApplyError(null);
              setApplySuccess(null);

              try {
                if (!authUser?.token) return;
                const itemsToApply = selectedItems.map((idx) => ({
                  action: actionItems[idx].action,
                  reasoning: actionItems[idx].reasoning,
                  task_type: actionItems[idx].task_type || "general",
                }));

                await applyUserActionItems(authUser.token, userId, { items: itemsToApply });

                // capture selected indexes locally BEFORE state resets
                const savedIndexes = [...selectedItems];

                // highlight them
                setJustSaved(savedIndexes);

                setApplySuccess("Selected items saved as tasks.");
                setSelectedItems([]);

                setTimeout(() => {
                  setActionItems((prev) =>
                    prev.filter((_, idx) => !savedIndexes.includes(idx))
                  );
                  setJustSaved([]);
                }, 600);


                setApplySuccess("Selected items saved as tasks.");
                setSelectedItems([]); // clear selection
              } catch (err: any) {
                console.error(err);
                setApplyError(err.message || "Failed to apply action items");
              } finally {
                setApplyLoading(false);
              }
            }}
          >
            {applyLoading ? <CircularProgress size={20} /> : "Save selected items"}
          </Button>

          {applyError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {applyError}
            </Alert>
          )}

          {applySuccess && (
            <Alert severity="success" sx={{ mt: 1 }}>
              {applySuccess}
            </Alert>
          )}
        </Box>
        )}


      </AccordionDetails>
    </Accordion>


    </Paper>
  );
}
